// src/pages/CareersPage.jsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";

export default function CareersPage() {
  const navigate = useNavigate();

  return (
    <div className="cr-root">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {/* HERO */}
      <section className="cr-hero">
        <button className="cr-back" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="cr-badge">Careers</div>
        <h1>Build the Future of <span className="grad">Authentic Commerce</span></h1>
        <p className="cr-lede">
          We're a small team solving a big problem: making every online purchase verifiably real.
          Join us in California or Vietnam.
        </p>
      </section>

      {/* VALUES */}
      <section className="cr-section">
        <div className="cr-section-inner">
          <div className="cr-section-label">Our Values</div>
          <h2>What We Believe <span className="grad">In</span></h2>

          <div className="cr-values">
            <div className="cr-value">
              <div className="cr-value-icon">🎯</div>
              <h3>Ship early, ship often</h3>
              <p>We move fast, test hypotheses, and improve based on real user feedback — not theory.</p>
            </div>
            <div className="cr-value">
              <div className="cr-value-icon">🤝</div>
              <h3>Trust is everything</h3>
              <p>We built JepTags on the belief that shoppers deserve to know what they're buying. Same goes internally.</p>
            </div>
            <div className="cr-value">
              <div className="cr-value-icon">🌏</div>
              <h3>Remote-friendly, globally</h3>
              <p>Teams in the US and Vietnam. Async by default. We hire talent wherever we find it.</p>
            </div>
            <div className="cr-value">
              <div className="cr-value-icon">🛠️</div>
              <h3>Craft matters</h3>
              <p>We care about clean code, clear design, and products that feel good to use — every detail counts.</p>
            </div>
          </div>
        </div>
      </section>

      {/* OPENINGS */}
      <section className="cr-section cr-section-alt">
        <div className="cr-section-inner">
          <div className="cr-section-label">Open Roles</div>
          <h2>Positions <span className="grad">Available</span></h2>

          <div className="cr-empty-card">
            <div className="cr-empty-icon">📬</div>
            <h3>No open positions right now</h3>
            <p>
              We don't have any openings at the moment, but we're always interested in hearing
              from talented people. If you'd like to introduce yourself, send your resume and a note
              about what you care about.
            </p>
            <a href="mailto:careers@jeptags.com?subject=Introduction%20-%20JepTags%20Careers"
               className="cr-btn-primary">
              ✉️ Send an Introduction
            </a>
          </div>
        </div>
      </section>

      {/* HOW WE HIRE */}
      <section className="cr-section">
        <div className="cr-section-inner">
          <div className="cr-section-label">Our Process</div>
          <h2>How We <span className="grad">Hire</span></h2>

          <div className="cr-process">
            <div className="cr-step">
              <div className="cr-step-num">1</div>
              <div>
                <h3>Initial conversation</h3>
                <p>A 30-minute chat to learn about you and share what we're working on.</p>
              </div>
            </div>
            <div className="cr-step">
              <div className="cr-step-num">2</div>
              <div>
                <h3>Take-home or live exercise</h3>
                <p>A small, paid project that mirrors the kind of work you'd actually do here. Usually 2–4 hours.</p>
              </div>
            </div>
            <div className="cr-step">
              <div className="cr-step-num">3</div>
              <div>
                <h3>Team conversations</h3>
                <p>Meet 2–3 future teammates. We talk craft, collaboration, and fit — both ways.</p>
              </div>
            </div>
            <div className="cr-step">
              <div className="cr-step-num">4</div>
              <div>
                <h3>Offer</h3>
                <p>Fast decision, competitive offer, clear next steps.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cr-contact">
        <h2>Want to stay in touch?</h2>
        <p>Email us and we'll let you know when openings match your skills.</p>
        <div className="cr-contact-btns">
          <a href="mailto:careers@jeptags.com" className="cr-btn-primary">Email Careers Team</a>
          <Link to="/about" className="cr-btn-secondary">Learn About Us</Link>
        </div>
      </section>
    </div>
  );
}

const STYLES = `
.cr-root *,.cr-root *::before,.cr-root *::after{margin:0;padding:0;box-sizing:border-box}
.cr-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;min-height:100vh;
}
.cr-root a{color:var(--cyan);text-decoration:none}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}

/* HERO */
.cr-hero{max-width:900px;margin:0 auto;padding:60px 24px 48px;text-align:center;position:relative}
.cr-back{position:absolute;top:60px;left:24px;display:inline-flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.cr-back:hover{border-color:var(--cyan);color:var(--cyan)}
.cr-back svg{width:14px;height:14px}
.cr-badge{display:inline-block;padding:8px 20px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:20px}
.cr-hero h1{font-size:clamp(36px,5vw,52px);font-weight:800;line-height:1.1;letter-spacing:-1.5px;margin-bottom:14px}
.cr-lede{font-size:17px;color:rgba(224,240,240,0.55);max-width:580px;margin:0 auto;line-height:1.7}

/* SECTION */
.cr-section{padding:48px 24px}
.cr-section-alt{background:rgba(15,33,64,0.3)}
.cr-section-inner{max-width:860px;margin:0 auto}
.cr-section-label{display:inline-block;padding:6px 14px;border-radius:100px;background:rgba(0,229,204,0.06);border:1px solid rgba(0,229,204,0.12);font-size:11px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:16px}
.cr-section h2{font-size:clamp(26px,4vw,36px);font-weight:800;line-height:1.2;letter-spacing:-.8px;margin-bottom:20px}

/* VALUES */
.cr-values{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:28px}
.cr-value{padding:24px;background:rgba(3,10,20,0.5);border:1px solid rgba(0,229,204,0.06);border-radius:16px}
.cr-value-icon{font-size:32px;margin-bottom:12px}
.cr-value h3{font-size:17px;font-weight:700;color:#e0f0f0;margin-bottom:8px}
.cr-value p{font-size:14px;line-height:1.7;color:rgba(224,240,240,0.55);margin:0}

/* EMPTY CARD */
.cr-empty-card{padding:48px 32px;background:rgba(3,10,20,0.5);border:1px dashed rgba(0,229,204,0.2);border-radius:20px;text-align:center;margin-top:28px}
.cr-empty-icon{font-size:48px;margin-bottom:16px}
.cr-empty-card h3{font-size:20px;font-weight:700;color:#e0f0f0;margin-bottom:12px}
.cr-empty-card p{font-size:15px;line-height:1.75;color:rgba(224,240,240,0.55);max-width:500px;margin:0 auto 24px}

/* PROCESS */
.cr-process{display:flex;flex-direction:column;gap:16px;margin-top:28px}
.cr-step{display:flex;gap:20px;align-items:flex-start;padding:22px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);border-radius:14px}
.cr-step-num{width:40px;height:40px;border-radius:50%;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);font-weight:800;font-size:16px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.cr-step h3{font-size:16px;font-weight:700;color:#e0f0f0;margin-bottom:6px}
.cr-step p{font-size:14px;line-height:1.65;color:rgba(224,240,240,0.55);margin:0}

/* BUTTONS */
.cr-btn-primary{display:inline-block;padding:14px 28px;border-radius:12px;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);font-weight:700;font-size:15px;transition:transform .25s}
.cr-btn-primary:hover{transform:translateY(-2px)}
.cr-btn-secondary{display:inline-block;padding:14px 28px;border-radius:12px;border:1px solid rgba(0,229,204,0.25);background:transparent;color:var(--cyan);font-weight:600;font-size:15px;transition:all .25s}
.cr-btn-secondary:hover{border-color:var(--cyan);background:rgba(0,229,204,0.04)}

/* CONTACT */
.cr-contact{max-width:860px;margin:0 auto;padding:40px 24px 100px;text-align:center}
.cr-contact h2{font-size:26px;font-weight:800;letter-spacing:-.5px;margin-bottom:10px}
.cr-contact p{font-size:15px;color:rgba(224,240,240,0.5);margin-bottom:24px}
.cr-contact-btns{display:flex;gap:12px;justify-content:center;flex-wrap:wrap}

@media(max-width:700px){
  .cr-back{position:static;margin-bottom:20px}
  .cr-hero{padding:40px 20px 32px}
  .cr-values{grid-template-columns:1fr}
  .cr-step{flex-direction:column;gap:12px}
}
`;
